import {
  AvailableLanguages,
  AvailableTimeSlotData,
  DatesWithPrioritizedProviders,
  PrioritizedDailyProviderAvailability,
  PrioritizedMonthlyProviderAvailability,
  Step,
  StepConfig,
  SupportedLocationType,
  getLocalTime,
} from '@enaratech/funnel-helper';

/**
 * @description Obtains the appointments for the most prioritized clinics and returns them formatted
 */
export const formatPrioritizedProvidersByDates = (
  prioritizedMonthlyAvailability: PrioritizedMonthlyProviderAvailability['dates']
) => {
  const prioritizedAvailability: DatesWithPrioritizedProviders = {};

  Object.entries(prioritizedMonthlyAvailability)
    .filter(([_, slotsData]) => slotsData.slotsByProvider.length > 0)
    .forEach(([date, slotsData]) => {
      prioritizedAvailability[date as string] = slotsData.slotsByProvider.map((p) => ({
        ...p.provider,
        acuityAppointmentTypeId: p.acuityAppointmentTypeId,
      }));
    });

  if (!Object.values(prioritizedAvailability).length) {
    return null;
  }

  return prioritizedAvailability;
};

/**
 * @description Return the SLOT as a key to be able to identify the time slot
 * @example dateTimeSlot_acuityAppointmentTypeId_acuityCalendarId1_acuityCalendarId2_acuityCalendarId3
 */
export const getTimeSlotKey = (slot: {
  acuityAppointmentTypeId: number;
  acuityCalendarIds: number[];
  dateTime: string;
}): string =>
  `${slot.dateTime}_${slot.acuityAppointmentTypeId}_${slot.acuityCalendarIds.join('_')}`;

/**
 * @param timeSlotKey Time slot key: timeSlot_acuityAppointmentTypeId_acuityCalendarId1_acuityCalendarId2_acuityCalendarId3
 */
export const getDataFromTimeSlotKey = (
  timeSlotKey: string
): {
  dateTime: string;
  acuityCalendarIds: number[];
  acuityAppointmentTypeId: number;
} => {
  const [dateTime, acuityAppointmentTypeId, ...acuityCalendarIds] = timeSlotKey.split('_');

  return {
    dateTime,
    acuityCalendarIds: acuityCalendarIds.map((ac) => Number(ac)),
    acuityAppointmentTypeId: Number(acuityAppointmentTypeId),
  };
};

/**
 * @description Match a step's config against an appointment code key (e.g. "IN-MD-OL-EN").
 * Single source of truth for step ↔ scheduled-appointment matching.
 */
export const stepMatchesAppointmentKey = (config: StepConfig, appointmentKey: string): boolean => {
  const [intention, specialty, location, language] = appointmentKey.split('-');
  if (!intention || !specialty || !location || !language) {
    return false;
  }
  return (
    intention === config.allowedIntention &&
    specialty === config.allowedSpecialty &&
    config.allowedLocations.includes(location as SupportedLocationType) &&
    config.allowedLanguages.includes(language.toLowerCase() as AvailableLanguages)
  );
};

export const findAppointmentKeyForStep = (
  step: Step,
  appointmentKeys: string[]
): string | undefined => appointmentKeys.find((key) => stepMatchesAppointmentKey(step.config, key));

/**
 * @description Reconcile backend-fetched steps with funnel-local recent bookings.
 *
 * The backend's `getStepsForMember` can still report a just-booked step as `scheduled: false`
 * until the Acuity webhook is processed (propagation lag of up to a couple of minutes). Left
 * uncorrected, that step is re-presented as `active` and the member books it again — the cause of
 * the observed duplicate IN-NS booking (two Nutrition appointments to different providers, ~2 min
 * apart). This helper:
 *   1. marks `scheduled = true` on any step covered by a recent local booking, and
 *   2. recomputes `active` to the first step that is neither scheduled nor skipped,
 * so a step we just booked is never offered again while the backend catches up.
 *
 * Must be applied wherever steps are (re)fetched and the active step is derived from them.
 */
export const reconcileStepsWithRecentBookings = (
  steps: Step[],
  recentAppointmentKeys: string[]
): Step[] => {
  if (!recentAppointmentKeys.length) {
    return steps;
  }

  const patched = steps.map((step) =>
    step.scheduled || !findAppointmentKeyForStep(step, recentAppointmentKeys)
      ? step
      : { ...step, scheduled: true }
  );

  const nextActiveStep = patched.find((step) => !step.scheduled && !step.skipped);

  return patched.map((step) => ({
    ...step,
    active: nextActiveStep ? step.position === nextActiveStep.position : false,
  }));
};

export const cleanAndFormatSlots = (
  availableAppointments: PrioritizedDailyProviderAvailability,
  patientTimezone: string
): AvailableTimeSlotData[] => {
  const slots: { [localDateTime: string]: AvailableTimeSlotData } = {};

  for (let i = 0; i < availableAppointments.slots.length; i++) {
    const slot = availableAppointments.slots[i];

    const localDateTime = getLocalTime({
      isoDateToConvert: slot.time,
      localTimezone: patientTimezone,
    });

    if (!slots[localDateTime]) {
      slots[localDateTime] = {
        acuityAppointmentTypeId: slot.slotsByProvider[0].acuityAppointmentTypeId,
        acuityCalendarIds: slot.slotsByProvider.map(({ provider }) => provider.acuityCalendarId),
        localDateTime,
        dateTime: slot.time,
      };
    }
  }

  return Object.values(slots);
};
